import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { cacheConfigForUserSpecificData } from '../hooks/api/queryConfig';
import { syncAlertsAcrossWindows } from '../lib/crossWindowAlertSync';

/**
 * Query Client Configuration
 *
 * Default settings match cacheConfigForUserSpecificData (most common case):
 * - staleTime: 5 minutes (data stays fresh for 5 minutes)
 * - gcTime: 10 minutes (keep in cache for navigation)
 * - refetchOnWindowFocus: false (user controls the data, no need to refetch)
 *
 * Hooks only override defaults when they need different behavior:
 * - cacheConfigForCollaborativeData: For multi-admin data (org policies, members)
 * - cacheConfigForStaticReferenceData: For rarely-changing data (org details, user profile)
 *
 * This approach reduces boilerplate - most hooks use the default and only
 * collaborative/static data hooks need to override.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      ...cacheConfigForUserSpecificData,
      retry: 1, // Default retry once (hooks can override)
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 0, // Don't retry mutations by default
    },
  },
});

interface QueryProviderProps {
  children: ReactNode;
}

/**
 * Query Provider Component
 *
 * Wraps the app with React Query context.
 * Should be placed inside AuthProvider to access Descope session.
 *
 * @example
 * <AuthProvider>
 *   <QueryProvider>
 *     <App />
 *   </QueryProvider>
 * </AuthProvider>
 */
export function QueryProvider({ children }: QueryProviderProps) {
  // The alerts table and an alert's pop-out window see each other's changes.
  useEffect(() => syncAlertsAcrossWindows(queryClient), []);
  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
